/**
 * Labstep
 *
 * @module components/FilterPost/Active
 * @desc Active Filter for Post
 */

import FilterPostActiveLabelRouter from 'labstep-web/components/FilterPost/Active/Label/Router';
import { FilterPostActiveOperator } from 'labstep-web/components/FilterPost/Active/Operator';
import { EXPERIMENT_WORKFLOW_POST_FILTER_URL_PARAMETERS } from 'labstep-web/constants/post-filter';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { useParamsStateContext } from 'labstep-web/contexts/params-state/hook';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import type { PostFilterNodeType } from 'labstep-web/models/entity-view.model';
import { LocationService } from 'labstep-web/services/location.service';
import type React from 'react';
import { withRouter } from 'react-router-dom';
import { FilterPostActiveLabel } from './Label';
import type {
  FilterPostActiveProps,
  PostFilterActiveContainerProps,
} from './types';

export const FilterPostActive: React.FC<FilterPostActiveProps> = ({
  searchParams,
  setPostFilterNodeType,
  removePostFilter,
  clearAll,
  clearParams,
  entityView,
  location,
  history,
}) => {
  const filter = searchParams?.filter as
    | PostFilterNodeType
    | undefined;
  if (!filter) {
    return null;
  }

  const labels: React.ReactNode[] = [];

  // We only support one level of filter nesting for now
  // filter[0] is the user-controlled filter
  const { type, predicates } = filter[0];
  predicates.forEach((predicate, i) => {
    labels.push(
      <FilterPostActiveLabel
        // FIXME react/no-array-index-key
        key={`label-${i}`}
        node={predicate}
        onRemoveClick={(): void => removePostFilter(i)}
        index={i}
        entityView={entityView}
        clearParams={clearParams}
        searchParams={searchParams}
      />,
    );
    const isNotLastItem = i !== predicates.length - 1;
    if (isNotLastItem) {
      labels.push(
        <FilterPostActiveOperator
          // FIXME react/no-array-index-key
          key={`operator-${i}`}
          type={type}
          setPostFilterNodeType={setPostFilterNodeType}
        />,
      );
    }
  });

  if (
    labels.length === 0 &&
    (!entityView ||
      !entityView?.context.includes('experiment_workflow_index') ||
      Object.keys(
        LocationService.getQueryParameters(location),
      ).filter((key) =>
        EXPERIMENT_WORKFLOW_POST_FILTER_URL_PARAMETERS.includes(key),
      ).length === 0)
  ) {
    return null;
  }

  let extraFilters = null;
  if (
    entityView &&
    entityView.context.includes('experiment_workflow_index')
  ) {
    extraFilters = <FilterPostActiveLabelRouter />;
  }

  return (
    <div
      style={{
        marginTop: '10px',
        display: 'flex',
        gap: '5px',
        alignItems: 'center',
      }}
    >
      <span>Filtering by:</span>
      {labels}
      {extraFilters}
      <ActionComponent
        type="text"
        text="Clear all"
        onClick={(): void => {
          LocationService.removeQueryParameters(
            history,
            location,
            EXPERIMENT_WORKFLOW_POST_FILTER_URL_PARAMETERS,
          );
          clearAll();
        }}
      />
    </div>
  );
};

export const PostFilterActiveContainer: React.FC<
  PostFilterActiveContainerProps
> = ({ searchParams, location, history }) => {
  const {
    setPostFilterNodeType,
    removePostFilter,
    clearAll,
    clearParams,
  } = useParamsContext();

  const { entityView } = useParamsStateContext();

  return (
    <FilterPostActive
      searchParams={searchParams}
      setPostFilterNodeType={setPostFilterNodeType}
      removePostFilter={removePostFilter}
      clearAll={clearAll}
      entityView={entityView}
      clearParams={clearParams}
      location={location}
      history={history}
    />
  );
};

export default withRouter(PostFilterActiveContainer);
